import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import {
  useGetPacingBoardShareableUsersQuery,
  useRevokePacingBoardShareMutation,
  useSharePacingBoardMutation,
} from '../../endpoints/pacing-board.endpoints';
import type { PacingBoardShare } from '../../types/pacing-board.types';

interface PacingBoardShareModalProps {
  open: boolean;
  onClose: () => void;
  boardId: string;
  shares: PacingBoardShare[];
}

export default function PacingBoardShareModal({
  open,
  onClose,
  boardId,
  shares,
}: PacingBoardShareModalProps) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'view' | 'edit'>('edit');
  const { data: users = [] } = useGetPacingBoardShareableUsersQuery(undefined, { skip: !open });
  const [shareBoard, { isLoading: isSharing }] = useSharePacingBoardMutation();
  const [revokeShare] = useRevokePacingBoardShareMutation();

  const sharedUserIds = new Set(shares.map((s) => s.user.id));
  const availableUsers = users.filter((u) => !sharedUserIds.has(u.id));

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    await shareBoard({ boardId, userId, role }).unwrap();
    setUserId('');
  };

  const handleRevoke = async (targetUserId: string) => {
    await revokeShare({ boardId, userId: targetUserId }).unwrap();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle>Share board</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-[#5E6278]">
            Share with Invisibly portal users (@invisibly email). They must already have portal access.
          </p>

          {shares.length > 0 && (
            <div className="space-y-2">
              <div className={portalDs.sectionLabel}>Current access</div>
              {shares.map((share) => (
                <div key={share.id} className="flex items-center justify-between rounded-lg border border-[#EDEEF2] px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-[#0D0F1A]">
                      {share.user.first_name} {share.user.last_name}
                    </div>
                    <div className="text-xs text-[#5E6278]">{share.user.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase text-[#9CA3AF]">{share.role}</span>
                    <PortalDsButton variant="ghost" size="sm" onClick={() => handleRevoke(share.user.id)}>
                      Revoke
                    </PortalDsButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleShare} className="space-y-3">
            <div className={portalDs.field}>
              <label className={portalDs.label} htmlFor="share-user">User</label>
              <select
                id="share-user"
                className={portalDs.select}
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              >
                <option value="">Select a user…</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
            </div>
            <div className={portalDs.field}>
              <label className={portalDs.label} htmlFor="share-role">Role</label>
              <select
                id="share-role"
                className={portalDs.select}
                value={role}
                onChange={(e) => setRole(e.target.value as 'view' | 'edit')}
              >
                <option value="edit">Edit — can add and modify budgets</option>
                <option value="view">View — can see budgets and alerts</option>
              </select>
            </div>
            <PortalDsButton variant="accent" type="submit" disabled={isSharing || !userId}>
              Invite user
            </PortalDsButton>
          </form>
        </div>

        <DialogFooter>
          <PortalDsButton variant="ghost" onClick={onClose}>Close</PortalDsButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
